/**
 * The decimal separator of a locale, e.g. `,` for it-IT and `.` for en-US.
 */
export function getDecimalSeparator(locale?: Intl.LocalesArgument): string {
  const parts = new Intl.NumberFormat(locale as string | string[] | undefined).formatToParts(1.1);
  return parts.find((part) => part.type === 'decimal')?.value ?? '.';
}

/**
 * Rewrites a typed `.` into the locale's decimal separator.
 *
 * In it-IT (and most of Europe) `.` is the *grouping* separator, so a field
 * parsing by locale reads `20.0` as 200 — and a field with a max then clamps
 * that to its bound, silently replacing what the user typed with a different,
 * plausible-looking number. Keyboards and numpads offer a dot, and people type
 * it, so the input has to mean what they intended.
 *
 * The one genuinely ambiguous shape is a three-digit tail (`1.234`), which is
 * exactly what valid grouping looks like; that is left alone. Any other tail
 * length cannot be grouping, so it is a decimal point.
 */
export function normalizeTypedDecimal(input: string, decimalSeparator: string): string {
  if (decimalSeparator === '.' || !input.includes('.')) {
    return input;
  }
  // Already using the locale separator: the dots are the user's own grouping.
  if (input.includes(decimalSeparator)) {
    return input;
  }
  const dotCount = (input.match(/\./g) ?? []).length;
  if (dotCount > 1) {
    return input;
  }
  const [, tail = ''] = input.split('.');
  if (tail.length === 3 && /^\d{3}$/.test(tail)) {
    return input;
  }
  return input.replace('.', decimalSeparator);
}
